import Link from "next/link";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";

import { subMonths } from "date-fns";
import { twMerge } from "tailwind-merge";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useActivityTracker from "hooks/useActivityTracker";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import SmartScoreTooltip from "./SmartScoreTooltip";

const VIEW_ACTIVITY_DELAY = 200;

type EntitySmartScoreProps = {
  entity: any;
  widget?: boolean;
  onDataLoaded?: (_isEmpty: boolean) => void;
};

/* eslint-disable unused-imports/no-unused-vars */
enum ScoreType {
  CountrySector = "Country & Sector",
  Sector = "Sector",
  Country = "Country",
}
/* eslint-enable unused-imports/no-unused-vars */

export const entitySmartScoreUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/smart-score`);

  return url.href;
};

export const entityClassificationDistributionUrl = (
  slug: string,
  startTime: Date
) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/entity-stat`);
  url.searchParams.append("time-scope", startTime.toISOString());
  url.searchParams.append("time-unit", "monthly");

  return url.href;
};

const EntitySmartScore = ({
  entity,
  widget,
  onDataLoaded,
}: EntitySmartScoreProps) => {
  const { mobile } = useMediaQuery();

  const { toggleModal } = useSignInSignUp();
  const { authenticated } = useAuthentication();
  const { trackActivity } = useActivityTracker();

  const [viewed, setViewed] = useState(false);

  const hoveringRef = useRef(false);

  const sentimentStart = useRef<Date>(subMonths(new Date(), 6));

  const showRealData = authenticated || true; // always show real smartscore data;

  const { data } = useApi(
    !showRealData || entity.smartScore
      ? null
      : entitySmartScoreUrl(entity.slug),
    baseFetcher
  );

  const trackViewActivity = useCallback(() => {
    if (mobile || viewed) return;

    trackActivity("smartscore.view", entity);
    setViewed(true);
  }, [entity, mobile, trackActivity, viewed]);

  const trackViewTooltipActivity = useCallback(
    (show) => {
      if (show) {
        trackActivity("smartscore.view_tooltip", entity, { target: "score" });
      }
    },
    [entity, trackActivity]
  );

  const smartScore = useMemo(
    () =>
      showRealData
        ? entity.smartScore || data
        : // mock data
          {
            countryAndSectorDividend: 3,
            countryAndSectorGrowth: 4,
            countryAndSectorMomentum: 3,
            countryAndSectorResilience: 5,
            countryAndSectorValue: 3,
          },
    [showRealData, data, entity.smartScore]
  );

  const { data: sentimentData } = useApi(
    showRealData
      ? entityClassificationDistributionUrl(entity.slug, sentimentStart.current)
      : null,
    baseFetcher
  );

  const [type, setType] = useState<ScoreType>(ScoreType.CountrySector);

  const totalBullish = showRealData
    ? sentimentData?.bullish?.reduce(
        (sum: number, item: any) => sum + item.points,
        0
      )
    : // mock data
      5;
  const totalBearish = showRealData
    ? sentimentData?.bearish?.reduce(
        (sum: number, item: any) => sum + item.points,
        0
      )
    : // mock data
      5;
  const sentimentAvailable = smartScore?.sentiment > 0;

  const {
    countryAndSectorDividend,
    countryAndSectorGrowth,
    countryAndSectorMomentum,
    countryAndSectorResilience,
    countryAndSectorScore,
    countryAndSectorValue,

    countryDividend,
    countryGrowth,
    countryMomentum,
    countryResilience,
    countryScore,
    countryValue,

    sectorDividend,
    sectorGrowth,
    sectorMomentum,
    sectorResilience,
    sectorScore,
    sectorValue,
  } = smartScore || {};

  const score = useMemo(() => {
    if (type === ScoreType.Country) {
      return countryScore;
    }

    if (type === ScoreType.Sector) {
      return sectorScore;
    }

    return countryAndSectorScore;
  }, [countryAndSectorScore, countryScore, sectorScore, type]);

  const value = useMemo(() => {
    if (type === ScoreType.Country) {
      return countryValue;
    }

    if (type === ScoreType.Sector) {
      return sectorValue;
    }

    return countryAndSectorValue;
  }, [countryAndSectorValue, countryValue, sectorValue, type]);

  const dividend = useMemo(() => {
    if (type === ScoreType.Country) {
      return countryDividend;
    }

    if (type === ScoreType.Sector) {
      return sectorDividend;
    }

    return countryAndSectorDividend;
  }, [countryAndSectorDividend, countryDividend, sectorDividend, type]);

  const growth = useMemo(() => {
    if (type === ScoreType.Country) {
      return countryGrowth;
    }

    if (type === ScoreType.Sector) {
      return sectorGrowth;
    }

    return countryAndSectorGrowth;
  }, [countryAndSectorGrowth, countryGrowth, sectorGrowth, type]);

  const resilience = useMemo(() => {
    if (type === ScoreType.Country) {
      return countryResilience;
    }

    if (type === ScoreType.Sector) {
      return sectorResilience;
    }

    return countryAndSectorResilience;
  }, [countryAndSectorResilience, countryResilience, sectorResilience, type]);

  const momentum = useMemo(() => {
    if (type === ScoreType.Country) {
      return countryMomentum;
    }

    if (type === ScoreType.Sector) {
      return sectorMomentum;
    }

    return countryAndSectorMomentum;
  }, [countryAndSectorMomentum, countryMomentum, sectorMomentum, type]);

  const dataNotAvailable = useMemo(
    () => (
      <>
        <br />
        <br />
        <i>Data not available</i>
      </>
    ),
    []
  );

  const scores = useMemo(() => {
    return [
      {
        name: "Value",
        tooltip: (
          <>
            Value indicates the Company&apos;s share price per unit of net
            assets compared to its peers
            <br /> <br />
            Latest Reported Price-to-Book
            {!value ? dataNotAvailable : null}
          </>
        ),
        value,
      },
      {
        name: "Dividend",
        tooltip: (
          <>
            Dividend indicates the Company&apos;s payout to shareholders
            (dividends) compared to its peers
            <br />
            <br />
            Latest Reported Dividend Yield
            {!dividend ? dataNotAvailable : null}
          </>
        ),
        value: dividend,
      },
      {
        name: "Growth",
        tooltip: (
          <>
            Growth indicates the Company&apos;s growth in earnings compared to
            its peers
            <br /> <br />
            3yr Earning-per-share CAGR
            {!growth ? dataNotAvailable : null}
          </>
        ),
        value: growth,
      },
      {
        name: "Resilience",
        tooltip: (
          <>
            Resilience indicates the Company&apos;s profitability and ability to
            meet debt obligations as compared to its peers.
            <br /> <br />
            Latest Reported Net-Debt to Equity and LTM Net Income Margin
            {!resilience ? dataNotAvailable : null}
          </>
        ),
        value: resilience,
      },
      {
        name: "Momentum",
        tooltip: (
          <>
            Momentum indicates the growth in the Company&apos;s Market
            Capitalisation compared to its peers
            <br /> <br />
            Market Capitalisation Growth
            {!momentum ? dataNotAvailable : null}
          </>
        ),
        value: momentum,
      },
    ];
  }, [dataNotAvailable, dividend, growth, momentum, resilience, value]);
  const availableScores = useMemo(() => {
    let rs = 0;
    if (value) rs++;
    if (dividend) rs++;
    if (growth) rs++;
    if (resilience) rs++;
    if (momentum) rs++;
    return rs;
  }, [dividend, growth, momentum, resilience, value]);

  const range = 4;

  useEffect(() => {
    onDataLoaded && onDataLoaded(availableScores < 3);
  }, [availableScores, onDataLoaded, smartScore]);

  if (!smartScore || availableScores < 3) return <></>;

  const content = (
    <div
      className={twMerge(widget && "p-[15px]")}
      onMouseEnter={() => {
        hoveringRef.current = true;
        setTimeout(() => {
          if (hoveringRef.current) trackViewActivity();
        }, VIEW_ACTIVITY_DELAY);
      }}
      onMouseLeave={() => {
        hoveringRef.current = false;
      }}
    >
      <div
        className={twMerge(
          "text-active-black text-[16px] font-medium leading-[34.5px]",
          "flex space-x-[10px]"
        )}
      >
        <div className="flex-1">
          <span className="mr-[5px]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="https://assets.smartkarma.com/icons/sparkle.svg"
              alt="Quiddity Northbound Monitor"
              width={20}
              height={22}
              className="pb-[2px]"
            />
          </span>
          Smartkarma SmartScore
        </div>
        {showRealData ? (
          <>
            <div className="ml-[16px] text-utah-sky font-bold">{score}/5</div>
          </>
        ) : (
          <span
            className="ml-[5px] font-normal text-[#b5485d] hover:text-[#b5485d] cursor-pointer"
            onClick={() => toggleModal("sign-up")}
          >
            (Sign Up to Access)
          </span>
        )}
      </div>
      <div
        className={twMerge(
          "text-[14px]",
          showRealData ? "text-smart-teal" : "text-active-black"
        )}
      >
        <div>
          <div
            className={twMerge(
              showRealData ? "text-active-black" : "text-shady-gray"
            )}
          >
            Relative to stocks in the same:
          </div>
          <div>
            <Dropdown>
              <Dropdown.Toggle
                disabled={!showRealData}
                className={twMerge(
                  showRealData ? "text-smart-teal" : "text-shady-gray",
                  " border-none bg-transparent p-0",
                  "after:hidden"
                )}
              >
                {type} <FontAwesomeIcon icon={faCaretDown} />
              </Dropdown.Toggle>

              <Dropdown.Menu
                className={"border-1 boder-solid border-smart-teal"}
              >
                {Object.values(ScoreType).map((scoreType) => (
                  <Dropdown.Item
                    key={scoreType}
                    className={twMerge(
                      "text-smart-teal",
                      "hover:text-white hover:bg-smart-teal",
                      scoreType === type && "text-white bg-smart-teal"
                    )}
                    onClick={() => setType(scoreType as ScoreType)}
                  >
                    {scoreType}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div className="mt-[6px] pr-[20px] space-y-[16px]">
          <div className="flex">
            <div className="min-w-[100px]" />
            <div className="flex-1 relative ml-[3px]">
              {Array.from(Array(range + 1).keys()).map((index) => {
                const left = `${(index / range) * 100}%`;

                return (
                  <div
                    key={index}
                    className="absolute ml-[-3px] border border-solid border-[#F5F5F5] w-0 z-0"
                    style={{
                      left,
                      top: 25,
                      height: 175,
                    }}
                  ></div>
                );
              })}
            </div>
          </div>
          {scores.map(({ name, tooltip, value }) => (
            <div key={name} className="flex z-1">
              <div className="min-w-[100px]">
                <SmartScoreTooltip
                  entity={entity}
                  name={name}
                  tooltip={tooltip}
                >
                  <span className="cursor-pointer">{name}</span>
                </SmartScoreTooltip>
              </div>
              <div className="flex-1 pt-[8px] drop-shadow-lg">
                {value && (
                  <div
                    className={twMerge(
                      showRealData ? "bg-utah-sky" : "bg-shady-gray",
                      "rounded-full h-[5px]",
                      "relative"
                    )}
                    style={{
                      width: `${((value - 1) / range) * 100}%`,
                    }}
                  >
                    <div
                      className={twMerge(
                        "absolute top-[-5px]",
                        value > 0 ? "right-[-6px]" : "left-[-6px]",
                        showRealData
                          ? "bg-utah-sky"
                          : "bg-white border border-solid border-intelligent-silver",
                        "w-[15px] h-[15px] rounded-full",
                        "text-white text-[12px] text-center leading-[16px]"
                      )}
                    >
                      {showRealData ? value : ""}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="flex">
            <div className="min-w-[100px]" />
            <div className="flex-1 relative mx-[2px]">
              {Array.from(Array(range + 1).keys()).map((index) => {
                const left = `${(index / range) * 100}%`;

                return (
                  <div
                    key={index}
                    className="absolute ml-[-3px] text-intelligent-silver"
                    style={{
                      left,
                    }}
                  >
                    {index + 1}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className={twMerge("mt-[44px] pr-[20px]", "flex")}>
          <div className="min-w-[100px]">
            <SmartScoreTooltip
              name="sentiment"
              entity={entity}
              tooltip={
                <div className="text-left">
                  Sentiment indicates Smartkarma analysts&apos; views on the
                  Company in recent months
                  <br />
                  <br />
                  Last 6 months Bullish vs Bearish Insights
                  {sentimentAvailable ? (
                    <>
                      <br />
                      <br />
                      <div className="mb-[5px] leading-[16px]">
                        <div>
                          <span className="inline-block bg-bullish-green rounded-full w-[13px] h-[13px] mt-[5px] mr-[5px]" />
                          Bullish - {totalBullish}
                        </div>
                        <div>
                          <span className="inline-block bg-bearish-red rounded-full w-[13px] h-[13px] mt-[5px] mr-[5px]" />
                          Bearish - {totalBearish}
                        </div>
                      </div>
                    </>
                  ) : (
                    dataNotAvailable
                  )}
                </div>
              }
            >
              <span className="cursor-pointer">Sentiment</span>
            </SmartScoreTooltip>
          </div>
          <div className="flex-1 flex pt-[8px]">
            <div
              className={twMerge(
                showRealData && sentimentAvailable
                  ? "bg-bullish-green"
                  : "bg-shady-gray",
                "rounded-full h-[5px]",
                "relative"
              )}
              style={{
                width: `${
                  sentimentAvailable ? (smartScore.sentiment - 1) * 25 : 50
                }%`,
              }}
            >
              <div
                className={twMerge(
                  "absolute top-[-1px] right-[-3px]",
                  "bg-active-black w-[6px] h-[7px]",
                  "z-10"
                )}
              ></div>
            </div>
            <div
              className={twMerge(
                showRealData && sentimentAvailable
                  ? "bg-bearish-red"
                  : "bg-shady-gray",
                "rounded-full h-[5px]",
                "relative"
              )}
              style={{
                width: `${
                  sentimentAvailable ? (5 - smartScore.sentiment) * 25 : 50
                }%`,
              }}
            ></div>
          </div>
        </div>
        <div className="mt-4 flex">
          <div className="flex-1">
            <Link href="/tools/smart-score-screener" className="font-semibold">
              View Screener »
            </Link>
          </div>

          <OverlayTrigger
            rootClose
            placement={mobile ? "bottom" : "top"}
            trigger={["hover", "focus"]}
            onToggle={trackViewTooltipActivity}
            overlay={
              <Tooltip>
                <div className="text-left">
                  SmartScore is a compound score for the Company indicating its
                  overall outlook
                  <br />
                  <br />
                  Equally weighted average of Factor scores
                </div>
              </Tooltip>
            }
          >
            <div>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-[12px] text-gray-gray mt-[7px] cursor-pointer"
              />
            </div>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  );

  if (widget) {
    return <CustomCard className="p-0">{content}</CustomCard>;
  }

  return content;
};

export default EntitySmartScore;
